import calcularSimilitud from "../../../../../Components/calcularSimilitud";

/**
 * Emparejamiento del texto libre de "MATERIAL A SOLICITAR" del formulario de Google
 * contra los insumos marcados como Libro Virtual.
 *
 * El texto del formulario cambia cada semestre y nunca coincide letra por letra con la
 * descripción del insumo ("Nivel 5 y 6 • Four Corners 3" vs "FOUR CORNERS 3 STUDENT
 * BOOK"), así que se sugiere el mejor candidato y la pantalla siempre deja corregirlo
 * a mano.
 */

export const normalizarTexto = (valor) =>
  String(valor === undefined || valor === null ? "" : valor)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Sólo se corta por viñeta/barra: un guion suele ser parte del nombre del libro.
const SEPARADORES = /[•·|]/;

/**
 * El nombre real del libro es el último segmento ("Nivel 5 y 6 • Four Corners 3" ->
 * "Four Corners 3"). El prefijo de niveles mete números que no existen en el catálogo
 * y arruinan cualquier comparación.
 */
export const segmentoLibro = (texto) => {
  const completo = String(texto || "").trim();
  const partes = completo
    .split(SEPARADORES)
    .map((parte) => parte.trim())
    .filter(Boolean);
  return partes.length > 1 ? partes[partes.length - 1] : completo;
};

const tokens = (textoNormalizado) =>
  textoNormalizado.split(" ").filter(Boolean);

const tieneDigito = (token) => /\d/.test(token);

/**
 * Los libros de una misma serie sólo se distinguen por el número ("Four Corners 1" vs
 * "Four Corners 4"), y ese token pesa igual que cualquier otro en una comparación por
 * traslape o por Levenshtein. Por eso es un requisito duro, no parte del puntaje: si un
 * token con dígito del material no está en el candidato, ese candidato no aplica.
 */
const cumpleTokensNumericos = (tokensMaterial, tokensCandidato) => {
  const numericos = tokensMaterial.filter(tieneDigito);
  if (numericos.length === 0) return true;
  return numericos.every((token) => tokensCandidato.includes(token));
};

const cobertura = (tokensMaterial, tokensCandidato) => {
  const palabras = tokensMaterial.filter((token) => !tieneDigito(token));
  if (palabras.length === 0) return 1;
  const encontradas = palabras.filter((token) => tokensCandidato.includes(token));
  return encontradas.length / palabras.length;
};

const COBERTURA_MINIMA = 0.6;
const SIMILITUD_MINIMA = 55;
// Si los dos mejores candidatos quedan casi igual de buenos no se sugiere ninguno:
// más vale que el usuario elija a que se cree la venta del libro equivocado.
const MARGEN_AMBIGUEDAD = 5;

const puntuarCandidato = (insumo, normTexto, normSegmento) => {
  const normDescripcion = normalizarTexto(insumo.descripcion);
  const normClave = normalizarTexto(insumo.clave);
  const normCodigo = normalizarTexto(insumo.codigo);

  const exactos = [normDescripcion, normClave, normCodigo].filter(Boolean);

  if (exactos.includes(normTexto)) return 100;
  if (exactos.includes(normSegmento)) return 96;

  if (!normDescripcion) return 0;

  const tokensMaterial = tokens(normSegmento);
  const tokensCandidato = tokens(
    [normDescripcion, normClave, normCodigo].filter(Boolean).join(" "),
  );

  if (!cumpleTokensNumericos(tokensMaterial, tokensCandidato)) return 0;
  if (cobertura(tokensMaterial, tokensCandidato) < COBERTURA_MINIMA) return 0;

  if (normDescripcion.includes(normSegmento)) return 90;
  if (normSegmento.includes(normDescripcion)) return 85;

  const similitud = calcularSimilitud(normSegmento, normDescripcion);
  return similitud >= SIMILITUD_MINIMA ? similitud : 0;
};

/**
 * @param {string} textoMaterial  el valor tal cual viene del Excel
 * @param {Array} insumos         insumos Libro Virtual del catálogo
 * @returns {Object|null}         el insumo sugerido, o null si no hay uno claro
 */
export const sugerirInsumoParaMaterial = (textoMaterial, insumos) => {
  const normTexto = normalizarTexto(textoMaterial);
  if (!normTexto || !Array.isArray(insumos) || insumos.length === 0) return null;

  const normSegmento = normalizarTexto(segmentoLibro(textoMaterial));

  const puntuados = insumos
    .map((insumo) => ({
      insumo,
      puntaje: puntuarCandidato(insumo, normTexto, normSegmento),
    }))
    .filter((candidato) => candidato.puntaje > 0)
    .sort((a, b) => b.puntaje - a.puntaje);

  if (puntuados.length === 0) return null;
  if (
    puntuados.length > 1 &&
    puntuados[0].puntaje - puntuados[1].puntaje < MARGEN_AMBIGUEDAD
  ) {
    return null;
  }

  return puntuados[0].insumo;
};

/**
 * Matrícula del Excel contra la del alumno. Se intenta, en orden:
 *  1. igualdad normalizada;
 *  2. sólo los dígitos, para las matrículas que vienen con prefijo capturado a mano
 *     ("B24690214", "#26690651", "INGL26690693") — y sólo si resuelve a UN alumno,
 *     porque si no sería adivinar;
 *  3. el correo, que en la escuela es la matrícula + dominio.
 */
export const buscarAlumnoPorMatricula = (matriculaExcel, correoExcel, indices) => {
  const normMatricula = normalizarTexto(matriculaExcel).replace(/ /g, "");
  if (normMatricula) {
    const exacto = indices.porMatricula.get(normMatricula);
    if (exacto) return { alumno: exacto, criterio: "matricula" };

    const soloDigitos = normMatricula.replace(/[^0-9]/g, "");
    if (soloDigitos) {
      const porDigitos = indices.porDigitos.get(soloDigitos);
      if (porDigitos && porDigitos.length === 1) {
        return { alumno: porDigitos[0], criterio: "digitos" };
      }
      if (porDigitos && porDigitos.length > 1) {
        return { alumno: null, criterio: "ambiguo" };
      }
    }
  }

  const normCorreo = String(correoExcel || "").trim().toLowerCase();
  if (normCorreo) {
    const porCorreo = indices.porCorreo.get(normCorreo);
    if (porCorreo && porCorreo.length === 1) {
      return { alumno: porCorreo[0], criterio: "correo" };
    }
  }

  return { alumno: null, criterio: "noEncontrado" };
};

/**
 * Índices de búsqueda de alumnos. Se arman una sola vez por archivo, no por fila.
 */
export const construirIndicesAlumnos = (alumnos) => {
  const porMatricula = new Map();
  const porDigitos = new Map();
  const porCorreo = new Map();

  (alumnos || []).forEach((alumno) => {
    const norm = normalizarTexto(alumno.matricula).replace(/ /g, "");
    if (norm && !porMatricula.has(norm)) porMatricula.set(norm, alumno);

    const digitos = norm.replace(/[^0-9]/g, "");
    if (digitos) {
      if (!porDigitos.has(digitos)) porDigitos.set(digitos, []);
      porDigitos.get(digitos).push(alumno);
    }

    const correo = String(alumno.correo || "").trim().toLowerCase();
    if (correo) {
      if (!porCorreo.has(correo)) porCorreo.set(correo, []);
      porCorreo.get(correo).push(alumno);
    }
  });

  return { porMatricula, porDigitos, porCorreo };
};
